/**
 * investorOnboarding.ts — Primary onboarding flow for the investor pivot.
 *
 * A new signup that picked "I'm an investor" on role select lands here
 * right after account creation. Four lightweight steps capture the
 * investment thesis, target markets, deal criteria, and current portfolio
 * size, then persist everything to users.investment_criteria via the
 * PATCH /users/me/investor endpoint.
 *
 * The existing buyer onboarding (`onboarding` screen) is untouched —
 * legacy buyer signups still land there. Investors go through this file
 * instead.
 */

import { api } from './api.js';
import { clearInvestorAssumptions } from './investorDetail.js';
import { navigateToHome } from './profile.js';
import { navigate } from './router.js';
import { state } from './state.js';
import type { InvestmentCriteria, InvestmentStrategy, InvestorFinancingType } from '../../shared/types.js';
import { showToast } from './ui.js';
import { FINANCING_PRESET_DEFAULTS } from '../../shared/financing.js';
import { formatPriceShort } from '../../shared/format.js';
import { validateInvestmentCriteria } from '../../shared/validation.js';

// ─────────────────────────────────────────────────────────────────────────
// State — a scratch object on state to track in-progress answers so the
// user can back out of a step without losing what they picked earlier.
// Cleared when the flow completes or the user signs out.
// ─────────────────────────────────────────────────────────────────────────

interface InvestorStash {
  step: 1 | 2 | 3 | 4;
  /** True when re-running the wizard to edit saved criteria (vs first-time
   * onboarding). Edit runs skip the portfolio-size question — it's already
   * answered and the coarse buckets can't represent values like 5. */
  isEdit: boolean;
  strategy?: InvestmentStrategy;
  target_markets: string[];
  min_price?: number;
  max_price?: number;
  min_cap_rate?: number;
  min_cash_on_cash?: number;
  min_dscr?: number;
  max_rehab_budget?: number;
  property_types: string[];
  financing_type?: InvestorFinancingType;
  portfolio_size?: number;
  /** Fine-tuned loan terms. Initialized from the financing preset when the
   * user picks a type; explicit values override the preset in the math. */
  down_payment_pct?: number;
  interest_rate?: number;
  loan_term_years?: number;
}

function stash(): InvestorStash {
  const s = state as unknown as { _investorStash?: InvestorStash };
  if (!s._investorStash) {
    // Seed from the saved profile when re-running onboarding to edit
    // (profile → "Update investment criteria"), so existing preferences
    // aren't wiped by the generic defaults — editing financing must not
    // reset strategy, markets, or floors.
    const c = (state.user?.investment_criteria as Record<string, unknown> | undefined) || {};
    const strArr = (v: unknown): string[] => (Array.isArray(v) ? v.filter(x => typeof x === 'string') : []);
    const numOr = (v: unknown, fallback: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
    s._investorStash = {
      step: 1,
      // A populated criteria object means this run is an edit, not
      // first-time onboarding — same signal navigateToHome() uses to
      // treat the user as onboarded.
      isEdit: Object.keys(c).length > 0,
      strategy: typeof c.strategy === 'string' ? (c.strategy as InvestmentStrategy) : undefined,
      target_markets: strArr(c.target_markets),
      property_types: strArr(c.property_types),
      min_price: numOr(c.min_price, 100_000),
      max_price: numOr(c.max_price, 500_000),
      min_cap_rate: numOr(c.min_cap_rate, 6),
      min_cash_on_cash: numOr(c.min_cash_on_cash, 8),
      max_rehab_budget: typeof c.max_rehab_budget === 'number' ? c.max_rehab_budget : undefined,
      min_dscr: typeof c.min_dscr === 'number' ? c.min_dscr : undefined,
      portfolio_size: typeof c.portfolio_size === 'number' ? c.portfolio_size : undefined,
      financing_type: c.financing_type as InvestorFinancingType | undefined,
      down_payment_pct: typeof c.down_payment_pct === 'number' ? c.down_payment_pct : undefined,
      interest_rate: typeof c.interest_rate === 'number' ? c.interest_rate : undefined,
      loan_term_years: typeof c.loan_term_years === 'number' ? c.loan_term_years : undefined,
    };
  }
  return s._investorStash;
}

function clearStash(): void {
  (state as unknown as { _investorStash?: InvestorStash })._investorStash = undefined;
}

// ─────────────────────────────────────────────────────────────────────────
// Step data — kept as pure data so the render functions stay declarative.
// ─────────────────────────────────────────────────────────────────────────

interface StrategyOption {
  value: InvestmentStrategy;
  label: string;
  blurb: string;
  icon: string;
}

const STRATEGIES: StrategyOption[] = [
  { value: 'buy_and_hold', label: 'Buy & Hold', blurb: 'Long-term rentals for cash flow + appreciation', icon: 'home' },
  { value: 'flip', label: 'Fix & Flip', blurb: 'Buy, renovate, resell in 6-12 months', icon: 'tool' },
  { value: 'brrrr', label: 'BRRRR', blurb: 'Buy, rehab, rent, refinance, repeat', icon: 'refresh-cw' },
  { value: 'short_term_rental', label: 'Short-Term Rental', blurb: 'Airbnb / vacation rentals', icon: 'calendar' },
  { value: 'wholesale', label: 'Wholesale', blurb: 'Assign contracts without closing', icon: 'zap' },
];

// Keep the initial market list focused on the metros the analyzer
// already covers well. We'll expand alongside rent-comp coverage.
const MARKETS = [
  'Houston',
  'Dallas',
  'Austin',
  'San Antonio',
  'Phoenix',
  'Atlanta',
  'Tampa',
  'Jacksonville',
  'Indianapolis',
  'Kansas City',
  'Memphis',
  'Cleveland',
];

const PROPERTY_TYPES: { value: string; label: string }[] = [
  { value: 'single_family', label: 'Single-Family' },
  { value: 'multi_family', label: 'Multi-Family' },
  { value: 'condo', label: 'Condo' },
  { value: 'townhouse', label: 'Townhouse' },
];

const FINANCING: { value: InvestorFinancingType; label: string; blurb: string }[] = [
  { value: 'dscr', label: 'DSCR loan', blurb: 'Qualify based on rental income, not personal W-2' },
  { value: 'conventional', label: 'Conventional', blurb: 'Standard investor mortgage, personal income required' },
  { value: 'hard_money', label: 'Hard Money', blurb: 'Short-term, asset-based, higher rate' },
  { value: 'cash', label: 'Cash', blurb: 'No financing — full purchase price' },
];

const PORTFOLIO_SIZES: { value: number; label: string }[] = [
  { value: 0, label: 'None — first deal' },
  { value: 1, label: '1 property' },
  { value: 3, label: '2-3 properties' },
  { value: 7, label: '4-10 properties' },
  { value: 15, label: '10+ properties' },
];

// ─────────────────────────────────────────────────────────────────────────
// Entry point + step dispatcher
// ─────────────────────────────────────────────────────────────────────────

export function renderInvestorOnboarding(): HTMLElement {
  const s = stash();
  const div = document.createElement('div');
  div.className = 'investor-onboarding fade-in';
  div.innerHTML = renderStep(s.step);
  // Focus management + any per-step wiring runs after the DOM is in place.
  setTimeout(() => {
    wireStep(s.step);
    if (window.feather) window.feather.replace();
  }, 0);
  return div;
}

function renderStep(step: 1 | 2 | 3 | 4): string {
  // Edit runs skip the portfolio question, so the wizard is 3 steps.
  const totalSteps = stash().isEdit ? 3 : 4;
  const stepLabel = `Step ${step} of ${totalSteps}`;
  let body = '';
  if (step === 1) body = renderStrategyStep();
  else if (step === 2) body = renderMarketsStep();
  else if (step === 3) body = renderCriteriaStep();
  else body = renderPortfolioStep();

  return `
    <div class="inv-onb-shell">
      <div class="inv-onb-head">
        <div class="inv-onb-progress">
          ${[1, 2, 3, 4].filter((i) => i <= totalSteps).map((i) => `<span class="inv-onb-dot${i <= step ? ' filled' : ''}"></span>`).join('')}
        </div>
        <div class="inv-onb-step-label">${stepLabel}</div>
      </div>
      ${body}
    </div>
  `;
}

// ─────────────────────────────────────────────────────────────────────────
// Step 1 — Investment strategy
// ─────────────────────────────────────────────────────────────────────────

function renderStrategyStep(): string {
  const s = stash();
  return `
    <h1 class="inv-onb-title">What's your strategy?</h1>
    <p class="inv-onb-blurb">Pick the approach that matches how you invest. You can change this later.</p>
    <div class="inv-onb-strategies">
      ${STRATEGIES.map(
        (opt) => `
        <button type="button" class="inv-onb-option${s.strategy === opt.value ? ' selected' : ''}" data-strategy="${opt.value}">
          <i data-feather="${opt.icon}" width="20"></i>
          <div class="inv-onb-option-body">
            <div class="inv-onb-option-label">${opt.label}</div>
            <div class="inv-onb-option-blurb">${opt.blurb}</div>
          </div>
        </button>
      `
      ).join('')}
    </div>
    <div class="inv-onb-actions">
      <button class="btn btn-primary btn-block btn-lg" id="inv-onb-next" ${s.strategy ? '' : 'disabled'}
              onclick="investorOnboardingNext()">Continue</button>
    </div>
  `;
}

// ─────────────────────────────────────────────────────────────────────────
// Step 2 — Target markets
// ─────────────────────────────────────────────────────────────────────────

function renderMarketsStep(): string {
  const s = stash();
  return `
    <h1 class="inv-onb-title">Where are you investing?</h1>
    <p class="inv-onb-blurb">Choose one or more target markets. We'll surface deals here first.</p>
    <div class="inv-onb-market-grid">
      ${MARKETS.map(
        (m) => `
        <button type="button" class="inv-onb-chip${s.target_markets.includes(m) ? ' selected' : ''}" data-market="${m}">
          ${m}
        </button>
      `
      ).join('')}
    </div>
    <p class="inv-onb-note">More markets coming soon.</p>
    <div class="inv-onb-actions">
      <button class="btn btn-outline btn-block" onclick="investorOnboardingBack()">Back</button>
      <button class="btn btn-primary btn-block btn-lg" id="inv-onb-next" ${
        s.target_markets.length ? '' : 'disabled'
      } onclick="investorOnboardingNext()">Continue</button>
    </div>
  `;
}

// ─────────────────────────────────────────────────────────────────────────
// Step 3 — Criteria (price, cap rate, cash-on-cash, property type, financing)
// ─────────────────────────────────────────────────────────────────────────

/**
 * Fine-tune inputs for the selected financing type. The numbers shown are
 * what every CoC, DSCR, and cash-flow figure in the app is computed with
 * until the user changes them here or drags a deal-analysis slider.
 * Hidden for cash (no loan to tune) and until a type is picked.
 */
function renderFinancingTuning(s: InvestorStash): string {
  const t = s.financing_type;
  if (!t || t === 'cash') return '';
  const preset = FINANCING_PRESET_DEFAULTS[t];
  const down = s.down_payment_pct ?? preset.down;
  const rate = s.interest_rate ?? preset.rate;
  const term = s.loan_term_years ?? preset.term;
  const termNote = t === 'hard_money' ? 'Hard money is modeled as an interest-only bridge — the brutal short-term math is the honest signal, not a bug.' : '';
  return `
    <div class="inv-onb-tuning">
      <div class="inv-onb-tuning-title">Your loan terms <span class="inv-onb-tuning-hint">drive every CoC &amp; DSCR number you see</span></div>
      <div class="inv-onb-tuning-grid">
        <label class="inv-onb-tuning-field">
          <span>Down payment %</span>
          <input type="number" id="inv-fin-down" class="input" min="0" max="100" step="1" value="${down}">
        </label>
        <label class="inv-onb-tuning-field">
          <span>Interest rate %</span>
          <input type="number" id="inv-fin-rate" class="input" min="0" max="25" step="0.125" value="${rate}">
        </label>
        <label class="inv-onb-tuning-field">
          <span>Term (years)</span>
          <input type="number" id="inv-fin-term" class="input" min="1" max="40" step="1" value="${term}">
        </label>
      </div>
      ${termNote ? `<div class="inv-onb-tuning-note">${termNote}</div>` : ''}
    </div>
  `;
}

function renderCriteriaStep(): string {
  const s = stash();
  const priceMin = s.min_price ?? 100_000;
  const priceMax = s.max_price ?? 500_000;
  const cap = s.min_cap_rate ?? 6;
  const coc = s.min_cash_on_cash ?? 8;
  return `
    <h1 class="inv-onb-title">Deal criteria</h1>
    <p class="inv-onb-blurb">Set the filters the investor feed will use by default.</p>

    <div class="inv-onb-field">
      <label class="inv-onb-label">Price range
        <span class="inv-onb-range-value" id="inv-price-display">${formatPriceShort(priceMin)} – ${formatPriceShort(priceMax)}</span>
      </label>
      <div class="inv-onb-dual-range">
        <input type="range" id="inv-price-min" min="50000" max="2000000" step="25000" value="${priceMin}"
               oninput="investorOnboardingOnPrice()">
        <input type="range" id="inv-price-max" min="50000" max="2000000" step="25000" value="${priceMax}"
               oninput="investorOnboardingOnPrice()">
      </div>
    </div>

    <div class="inv-onb-field">
      <label class="inv-onb-label">Minimum cap rate
        <span class="inv-onb-range-value" id="inv-cap-display">${cap.toFixed(1)}%</span>
      </label>
      <input type="range" id="inv-cap" min="4" max="12" step="0.5" value="${cap}"
             oninput="investorOnboardingOnCap()">
    </div>

    <div class="inv-onb-field">
      <label class="inv-onb-label">Minimum cash-on-cash
        <span class="inv-onb-range-value" id="inv-coc-display">${coc.toFixed(1)}%</span>
      </label>
      <input type="range" id="inv-coc" min="4" max="20" step="0.5" value="${coc}"
             oninput="investorOnboardingOnCoc()">
    </div>

    <div class="inv-onb-field">
      <label class="inv-onb-label">Property types</label>
      <div class="inv-onb-chip-row">
        ${PROPERTY_TYPES.map(
          (t) => `
          <button type="button" class="inv-onb-chip${s.property_types.includes(t.value) ? ' selected' : ''}" data-ptype="${t.value}">
            ${t.label}
          </button>
        `
        ).join('')}
      </div>
    </div>

    <div class="inv-onb-field">
      <label class="inv-onb-label">Financing</label>
      <div class="inv-onb-financing">
        ${FINANCING.map(
          (f) => `
          <button type="button" class="inv-onb-option${s.financing_type === f.value ? ' selected' : ''}" data-financing="${f.value}">
            <div class="inv-onb-option-body">
              <div class="inv-onb-option-label">${f.label}</div>
              <div class="inv-onb-option-blurb">${f.blurb}</div>
            </div>
          </button>
        `
        ).join('')}
      </div>
      ${renderFinancingTuning(s)}
    </div>

    <div class="inv-onb-actions">
      <button class="btn btn-outline btn-block" onclick="investorOnboardingBack()">Back</button>
      <button class="btn btn-primary btn-block btn-lg" onclick="investorOnboardingNext()">${s.isEdit ? 'Save changes' : 'Continue'}</button>
    </div>
  `;
}

// ─────────────────────────────────────────────────────────────────────────
// Step 4 — Portfolio size
// ─────────────────────────────────────────────────────────────────────────

function renderPortfolioStep(): string {
  const s = stash();
  return `
    <h1 class="inv-onb-title">Current portfolio</h1>
    <p class="inv-onb-blurb">How many investment properties do you own today?</p>
    <div class="inv-onb-portfolio-list">
      ${PORTFOLIO_SIZES.map(
        (p) => `
        <button type="button" class="inv-onb-option${s.portfolio_size === p.value ? ' selected' : ''}" data-portfolio="${p.value}">
          <div class="inv-onb-option-label">${p.label}</div>
        </button>
      `
      ).join('')}
    </div>
    <p class="inv-onb-note">You can add your properties in the Portfolio tab after setup.</p>
    <div class="inv-onb-actions">
      <button class="btn btn-outline btn-block" onclick="investorOnboardingBack()">Back</button>
      <button class="btn btn-primary btn-block btn-lg" id="inv-onb-finish"
              ${s.portfolio_size === undefined ? 'disabled' : ''}
              onclick="investorOnboardingFinish()">Finish</button>
    </div>
  `;
}

// ─────────────────────────────────────────────────────────────────────────
// Step wiring — per-step click handlers that mutate the stash + re-render.
// Kept simple: mutate + call navigate('investorOnboarding') to repaint.
// ─────────────────────────────────────────────────────────────────────────

function wireStep(step: number): void {
  const s = stash();
  if (step === 1) {
    document.querySelectorAll('.inv-onb-option[data-strategy]').forEach((el) => {
      el.addEventListener('click', () => {
        s.strategy = el.getAttribute('data-strategy') as InvestmentStrategy;
        rerender();
      });
    });
  } else if (step === 2) {
    document.querySelectorAll('.inv-onb-chip[data-market]').forEach((el) => {
      el.addEventListener('click', () => {
        const m = el.getAttribute('data-market');
        if (!m) return;
        const idx = s.target_markets.indexOf(m);
        if (idx >= 0) s.target_markets.splice(idx, 1);
        else s.target_markets.push(m);
        rerender();
      });
    });
  } else if (step === 3) {
    document.querySelectorAll('.inv-onb-chip[data-ptype]').forEach((el) => {
      el.addEventListener('click', () => {
        const v = el.getAttribute('data-ptype');
        if (!v) return;
        const idx = s.property_types.indexOf(v);
        if (idx >= 0) s.property_types.splice(idx, 1);
        else s.property_types.push(v);
        el.classList.toggle('selected');
      });
    });
    document.querySelectorAll('.inv-onb-option[data-financing]').forEach((el) => {
      el.addEventListener('click', () => {
        const v = el.getAttribute('data-financing') as InvestorFinancingType;
        s.financing_type = v;
        // Switching types resets the fine-tuned numbers to the new preset
        // so the inputs always describe the selected loan honestly.
        if (v === 'cash') {
          s.down_payment_pct = undefined;
          s.interest_rate = undefined;
          s.loan_term_years = undefined;
        } else {
          const preset = FINANCING_PRESET_DEFAULTS[v];
          s.down_payment_pct = preset.down;
          s.interest_rate = preset.rate;
          s.loan_term_years = preset.term;
        }
        rerender();
      });
    });
    // Fine-tune inputs persist into the stash on every keystroke so
    // Continue/Back never loses them. An emptied field is undefined, not
    // 0 — the key is then omitted from the PATCH so the financing preset
    // applies (0% down / 0% rate / 0-year term would be stored and every
    // card would compute on it).
    const numOrUndef = (v: string): number | undefined => (v === '' ? undefined : Number(v));
    const downEl = document.getElementById('inv-fin-down') as HTMLInputElement | null;
    const rateEl = document.getElementById('inv-fin-rate') as HTMLInputElement | null;
    const termEl = document.getElementById('inv-fin-term') as HTMLInputElement | null;
    if (downEl) downEl.addEventListener('input', () => { s.down_payment_pct = numOrUndef(downEl.value); });
    if (rateEl) rateEl.addEventListener('input', () => { s.interest_rate = numOrUndef(rateEl.value); });
    if (termEl) termEl.addEventListener('input', () => { s.loan_term_years = numOrUndef(termEl.value); });
  } else if (step === 4) {
    document.querySelectorAll('.inv-onb-option[data-portfolio]').forEach((el) => {
      el.addEventListener('click', () => {
        s.portfolio_size = Number(el.getAttribute('data-portfolio'));
        rerender();
      });
    });
  }
}

function rerender(): void {
  navigate('investorOnboarding');
}

// ─────────────────────────────────────────────────────────────────────────
// Slider handlers exported to window for inline oninput bindings.
// ─────────────────────────────────────────────────────────────────────────

export function investorOnboardingOnPrice(): void {
  const s = stash();
  const minEl = document.getElementById('inv-price-min') as HTMLInputElement | null;
  const maxEl = document.getElementById('inv-price-max') as HTMLInputElement | null;
  if (!minEl || !maxEl) return;
  let min = Number(minEl.value);
  let max = Number(maxEl.value);
  // Keep min ≤ max by dragging the closer slider rather than bouncing.
  if (min > max) {
    if (document.activeElement === minEl) max = min;
    else min = max;
    minEl.value = String(min);
    maxEl.value = String(max);
  }
  s.min_price = min;
  s.max_price = max;
  const display = document.getElementById('inv-price-display');
  if (display) display.textContent = `${formatPriceShort(min)} – ${formatPriceShort(max)}`;
}

export function investorOnboardingOnCap(): void {
  const s = stash();
  const el = document.getElementById('inv-cap') as HTMLInputElement | null;
  const display = document.getElementById('inv-cap-display');
  if (el) s.min_cap_rate = Number(el.value);
  if (display && s.min_cap_rate !== undefined) display.textContent = `${s.min_cap_rate.toFixed(1)}%`;
}

export function investorOnboardingOnCoc(): void {
  const s = stash();
  const el = document.getElementById('inv-coc') as HTMLInputElement | null;
  const display = document.getElementById('inv-coc-display');
  if (el) s.min_cash_on_cash = Number(el.value);
  if (display && s.min_cash_on_cash !== undefined) display.textContent = `${s.min_cash_on_cash.toFixed(1)}%`;
}

// ─────────────────────────────────────────────────────────────────────────
// Navigation — advance, back, finish.
// ─────────────────────────────────────────────────────────────────────────

export function investorOnboardingNext(): void {
  const s = stash();
  // Edit runs end at step 3: the portfolio question is skipped so a
  // criteria tweak can never clobber the saved portfolio size.
  if (s.isEdit && s.step === 3) {
    void investorOnboardingFinish();
    return;
  }
  if (s.step < 4) {
    s.step = (s.step + 1) as 1 | 2 | 3 | 4;
    rerender();
  }
}

export function investorOnboardingBack(): void {
  const s = stash();
  if (s.step > 1) {
    s.step = (s.step - 1) as 1 | 2 | 3 | 4;
    rerender();
  }
}

export async function investorOnboardingFinish(): Promise<void> {
  const s = stash();

  // PUT /users/me/investor replaces the whole criteria dict, so keys this
  // wizard has no input for (tax_bracket_pct, anything added later) must
  // ride along from the saved profile or they are silently deleted. Loan
  // terms are re-added below only for financed profiles — a switch to
  // cash must not inherit the old down / rate / term.
  const LOAN_TERM_KEYS = new Set(['down_payment_pct', 'interest_rate', 'loan_term_years']);
  const carried = Object.fromEntries(
    Object.entries((state.user?.investment_criteria ?? {}) as Record<string, unknown>)
      .filter(([k]) => !LOAN_TERM_KEYS.has(k)),
  ) as Partial<InvestmentCriteria>;
  const criteria: InvestmentCriteria = {
    ...carried,
    strategy: s.strategy,
    target_markets: s.target_markets,
    min_price: s.min_price,
    max_price: s.max_price,
    min_cap_rate: s.min_cap_rate,
    min_cash_on_cash: s.min_cash_on_cash,
    min_dscr: s.min_dscr,
    max_rehab_budget: s.max_rehab_budget,
    property_types: s.property_types,
    financing_type: s.financing_type,
    portfolio_size: s.portfolio_size,
  };
  // Fine-tuned loan terms travel with the profile so every CoC/DSCR
  // figure in the app is computed with the user's actual loan. Cash
  // needs no numbers — the backend preset covers it.
  if (s.financing_type && s.financing_type !== 'cash') {
    if (s.down_payment_pct !== undefined) criteria.down_payment_pct = s.down_payment_pct;
    if (s.interest_rate !== undefined) criteria.interest_rate = s.interest_rate;
    if (s.loan_term_years !== undefined) criteria.loan_term_years = s.loan_term_years;
  }

  // Validate before anything is stored (same shared check the mobile
  // wizard runs): the number inputs accept 150% down or a blank term,
  // and a stored out-of-range profile makes /investment-analysis/custom
  // 422 on every detail page ("Failed to load analysis").
  const errors = validateInvestmentCriteria(criteria);
  if (errors.length > 0) {
    showToast(errors[0], 'error');
    return;
  }

  try {
    await api('PATCH', '/users/me/investor', {
      user_type: 'investor',
      investment_criteria: criteria,
    });
    // Sync local user cache so subsequent UI routes by investor.
    if (state.user) {
      (state.user as { user_type?: string }).user_type = 'investor';
      (state.user as { investment_criteria?: InvestmentCriteria }).investment_criteria = criteria;
      localStorage.setItem('swipehome_user', JSON.stringify(state.user));
      // Mark onboarding complete in the same localStorage slot the buyer
      // flow uses so navigateToHome() short-circuits on subsequent visits.
      // Keeps the two completion signals in sync after the convergence.
      localStorage.setItem(`swipehome_onboarding_complete_${state.user.id}`, '1');
    }
    clearStash();
    // First-time onboarding gets the welcome; criteria edits get a
    // confirmation so existing users aren't "welcomed" on every tweak.
    showToast(s.isEdit ? 'Investment criteria updated' : 'Welcome to SwipeHome', 'success');
    // Every cached deal number is stale now: drop per-listing slider
    // state and force the feed to refetch so cards re-render under the
    // new financing profile.
    clearInvestorAssumptions();
    for (const mode of Object.values(state.feeds)) {
      mode.items = [];
      mode.loaded = false;
    }
    navigateToHome();
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Could not save investor profile';
    showToast(msg, 'error');
  }
}
